import { ShieldAlert, Siren, BookMarked } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarClock, ListChecks, Gift, Upload, Unlock, Wallet, Receipt, Timer, Wrench } from "lucide-react";
import { fmtLeft, useCountdown } from "@/lib/screen";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { money, fmtDate, greeting } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useHub } from "@/components/AppShell";
import { Loader, PageHeader, EmptyState } from "@/components/common";
import { NAV_GROUPS, canSee } from "@/lib/nav";
import UploadBillDialog from "@/components/bills/UploadBillDialog";
import PiggyStage from "@/components/piggy/PiggyStage";
import { useLiveRefresh } from "@/lib/live";
import WeatherCard from "@/components/weather/WeatherCard";
import ComingUpCard from "@/components/calendar/ComingUpCard";
import TonightCard from "@/components/meals/TonightCard";
import { OnThisDay, WhoStrip } from "@/components/home/FamilyLifeCards";
import { VoiceButton } from "@/components/voice/VoiceButton";
import { TenurePicker } from "@/components/family/FamilySections";

function TopCards({ hub }) {
  const cal = hub.my_access?.calendar;
  return (
    <div className={`mb-6 grid grid-cols-1 gap-4 [&>*]:min-w-0 ${cal ? "lg:grid-cols-2" : ""}`} data-testid="home-top-cards">
      <WeatherCard hub={hub} />
      {cal && <ComingUpCard hub={hub} />}
      {hub.my_access?.meals && <div className={cal ? "lg:col-span-2" : ""}><TonightCard hub={hub} /></div>}
    </div>
  );
}

function Stat({ label, value, rgb, testid, hint }) {
  return (
    <div className="q-card relative overflow-hidden q-rise" style={{ boxShadow: `0 0 30px rgba(${rgb},0.06)` }}>
      <span className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, rgba(${rgb},0.8), transparent)` }} />
      <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: `rgb(${rgb})` }}>{label}</div>
      <div className="mt-3 font-display text-3xl font-extrabold tracking-tight text-q-text sm:text-4xl" data-testid={testid}>{value}</div>
      {hint && <div className="mt-1 text-xs text-q-faint">{hint}</div>}
    </div>
  );
}

function KidCard({ to, Icon, label, children, testid, i }) {
  return (
    <Link to={to} className="block rounded-3xl border-2 bg-q-surface p-7 transition-transform duration-150 hover:-translate-y-1 q-rise" data-testid={testid}
      style={{ animationDelay: `${i * 80}ms`, borderColor: "rgba(var(--glow-kids),0.35)", boxShadow: "0 0 34px rgba(var(--glow-kids),0.18)" }}>
      <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl" style={{ background: "rgba(var(--glow-kids),0.16)" }}>
        <Icon className="h-8 w-8" style={{ color: "rgb(var(--glow-kids))" }} />
      </span>
      <h2 className="mt-5 font-display text-xl font-extrabold text-q-muted">{label}</h2>
      {children}
    </Link>
  );
}

function KidScreenCard({ to, screen, serverNow }) {
  const left = useCountdown(screen.session, serverNow);
  return (
    <KidCard to={to} Icon={Timer} label={screen.session ? "Screen time left" : "Screen-time minutes"} testid="kid-card-screen" i={3}>
      <p className="mt-1 font-display text-5xl font-extrabold tabular-nums text-q-text" data-testid="kid-home-screen">{screen.session ? fmtLeft(left) : screen.minutes}</p>
      <p className="mt-1 text-q-muted">{screen.session ? "Screen time is running" : "minutes in your time bank"}</p>
    </KidCard>
  );
}

function KidHome({ name, hub }) {
  const { session } = useAuth();
  const [s, setS] = useState(null);
  const loadS = useCallback(() => api.get(`/organizations/${hub.id}/kids/summary`).then(({ data }) => setS(data)), [hub.id]);
  useEffect(() => { loadS(); }, [loadS]);
  useLiveRefresh(() => loadS().catch(() => {}), (m) => ["screen_time", "piggy.ledger", "kid_request"].includes(m.entity), null);
  const base = `/h/${hub.id}/kids`;
  const w = s?.closest_wish;
  return (
    <div data-testid="kid-home">
      <h1 className="font-display text-5xl font-extrabold tracking-tight text-q-text sm:text-6xl q-rise q-onwall" data-testid="kid-home-greeting">Hi {name}!</h1>
      <div className="mt-8"><TopCards hub={hub} /></div>
      <WhoStrip hub={hub} />
      <OnThisDay hub={hub} />
      {!s ? <Loader /> : (
        <div className="mt-4 grid gap-5 sm:grid-cols-3">
          <KidCard to={`${base}/chores`} Icon={ListChecks} label="Chores left today" testid="kid-card-chores" i={0}>
            <p className="mt-1 font-display text-5xl font-extrabold text-q-text" data-testid="kid-home-chores-left">{s.chores_left_today.length}</p>
            <p className="mt-1 text-q-muted">{s.chores_left_today.length ? "Tap to get them done!" : "All done for today!"}</p>
          </KidCard>
          <div className="rounded-3xl border-2 bg-q-surface p-5 q-rise" data-testid="kid-card-piggy-bank" style={{ animationDelay: "80ms", borderColor: "rgba(var(--glow-kids),0.35)", boxShadow: "0 0 34px rgba(var(--glow-kids),0.18)" }}>
            <Link to={`${base}/piggy-bank`} className="font-display text-xl font-extrabold text-q-muted hover:text-q-text" data-testid="kid-home-piggy-link">My piggy bank</Link>
            <span className="sr-only" data-testid="kid-home-balance">{money(s.balance)}</span>
            {s.jars?.on ? (
              <div className="mt-2" data-testid="kid-home-jars">
                <PiggyStage hubId={hub.id} kidId={(session.kid || hub.acting_kid)?.id} size="md" />
                <p className="text-center text-sm font-bold text-q-text">Spend {money(s.jars.spend)} · Save {money(s.jars.save)} · Give {money(s.jars.give)}</p>
                <p className="text-center font-display text-lg font-extrabold text-q-text" data-testid="kid-home-jars-total">Total {money(s.jars.total)}</p>
              </div>
            ) : <PiggyStage hubId={hub.id} kidId={(session.kid || hub.acting_kid)?.id} size="md" />}
          </div>
          {s.screen?.on && <KidScreenCard to={`${base}/screen-time`} screen={s.screen} serverNow={s.server_now} />}
          <KidCard to={`${base}/wish-list`} Icon={Gift} label="Closest wish" testid="kid-card-wish-list" i={2}>
            {w ? (
              <>
                <p className="mt-1 truncate font-display text-2xl font-extrabold text-q-text" data-testid="kid-home-wish-name">{w.name}</p>
                <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-q-raised"><div className="h-full rounded-full" style={{ width: `${Math.max(2, w.progress)}%`, background: "rgb(var(--glow-kids))" }} /></div>
                <p className="mt-2 font-bold" style={{ color: w.can_afford ? "rgb(var(--glow-money))" : "rgb(var(--q-muted))" }} data-testid="kid-home-wish-status">{w.can_afford ? "You can afford this!" : `${money(w.to_go)} to go`}</p>
              </>
            ) : <p className="mt-1 text-q-muted">Add something you're saving for.</p>}
          </KidCard>
        </div>
      )}
    </div>
  );
}

function ApprovalsCard({ hub }) {
  const [n, setN] = useState(null);
  useEffect(() => { api.get(`/organizations/${hub.id}/kids/summary`).then(({ data }) => setN(data.pending_approvals)); }, [hub.id]);
  if (n === null) return null;
  return (
    <Link to={`/h/${hub.id}/kids/chores#approvals`} className="q-card mb-6 flex items-center gap-4 transition-transform duration-150 hover:-translate-y-0.5 q-rise" data-testid="home-approvals-card"
      style={{ borderColor: n ? "rgba(var(--glow-kids),0.45)" : undefined }}>
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: "rgba(var(--glow-kids),0.14)" }}><ListChecks className="h-6 w-6" style={{ color: "rgb(var(--glow-kids))" }} /></span>
      <div className="flex-1">
        <p className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: "rgb(var(--glow-kids))" }}>Waiting for approval</p>
        <p className="text-sm text-q-muted">{n ? "Chores, wishes, Save, Give and screen-time requests" : "Nothing waiting right now"}</p>
      </div>
      <span className="font-display text-4xl font-extrabold text-q-text" data-testid="home-approvals-count">{n}</span>
    </Link>
  );
}

function HomeCareCard({ hub, n }) {
  const rgb = "var(--glow-home)";
  return (
    <Link to={`/h/${hub.id}/home-care`} className="q-card mb-6 flex items-center gap-4 transition-transform duration-150 hover:-translate-y-0.5 q-rise" data-testid="home-care-card"
      style={{ borderColor: n ? `rgba(${rgb},0.45)` : undefined }}>
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: `rgba(${rgb},0.14)` }}><Wrench className="h-6 w-6" style={{ color: `rgb(${rgb})` }} /></span>
      <div className="flex-1">
        <p className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: `rgb(${rgb})` }}>Home care</p>
        <p className="text-sm text-q-muted">{n ? `${n} overdue ${n === 1 ? "task" : "tasks"}` : "Nothing overdue"}</p>
      </div>
      <span className="font-display text-4xl font-extrabold text-q-text" data-testid="home-care-overdue-count">{n}</span>
    </Link>
  );
}

function WelcomeCard({ hub }) {
  const groups = NAV_GROUPS.filter((g) => g.section && g.section !== "family" && g.section !== "money" && canSee(g.section, hub));
  return (
    <div data-testid="relative-home">
      <div className="q-card relative overflow-hidden p-8 sm:p-10 q-rise" data-testid="welcome-card" style={{ boxShadow: "0 0 50px rgba(var(--glow-home),0.08)" }}>
        <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full opacity-20 blur-3xl" style={{ background: "rgb(var(--glow-home))" }} />
        <p className="q-eyebrow">Welcome to</p>
        <h2 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-q-wordmark sm:text-5xl" data-testid="welcome-hub-name">{hub.name}</h2>
        <p className="mt-4 max-w-lg text-q-muted">This is your family's home base. Here's what you'll find here as the hub grows.</p>
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {groups.map((g) => (
          <div key={g.label} className="q-card p-5 q-rise">
            <g.Icon className="h-5 w-5" style={{ color: `rgb(${g.glowRgb})` }} />
            <div className="mt-3 text-[10px] font-black uppercase tracking-[0.16em]" style={{ color: `rgb(${g.glowRgb})` }}>{g.label}</div>
            <div className="mt-1 text-sm text-q-muted">{g.items.some((x) => !x.comingSoon) ? "Ready to use" : "Coming soon"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LockAlerts({ alerts, hubId, onDone }) {
  const unlock = async (kidId, name) => {
    try {
      await api.post(`/organizations/${hubId}/kids/${kidId}/unlock`);
      toast.success(`${name} can sign in again`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return alerts.map((a) => (
    <div key={a.kid_id} className="mb-4 flex flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center" data-testid={`alert-kid-locked-${a.kid_id}`}
      style={{ borderColor: "rgba(var(--glow-kids),0.4)", background: "rgba(var(--glow-kids),0.08)" }}>
      <AlertTriangle className="h-5 w-5 flex-shrink-0" style={{ color: "rgb(var(--glow-kids))" }} />
      <p className="flex-1 text-sm font-semibold text-q-text" data-testid="alert-kid-locked-message">{a.message}</p>
      <button className="q-btn q-btn-secondary py-2" onClick={() => unlock(a.kid_id, a.kid_name)} data-testid={`alert-unlock-btn-${a.kid_id}`}>
        <Unlock className="h-4 w-4" /> Unlock
      </button>
    </div>
  ));
}

function MoneyHome({ data, hub, onUpload }) {
  const m = data.money;
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Due this month" value={money(m.due_this_month)} rgb="var(--glow-money)" testid="home-due-this-month" hint="Unpaid bills due this month" />
        <Stat label="Overdue" value={m.overdue_count} rgb="var(--glow-media)" testid="home-overdue-count" hint={m.overdue_count === 1 ? "bill past its due date" : "bills past their due date"} />
        <Stat label="Spent this month" value={money(m.spent_this_month)} rgb="var(--glow-calendar)" testid="home-spent-this-month" hint="All spending, including paid bills" />
      </div>
      {m.budgets && (
        <Link to={`/h/${hub.id}/money/budgets`} className="mt-3 inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-q-muted hover:text-q-text" data-testid="home-budgets-line">
          Budgets: {m.budgets.pct}% used this month{m.budgets.over ? <span className="text-red-300" data-testid="home-budgets-over"> · {m.budgets.over} over budget</span> : null}
        </Link>
      )}
      <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <section className="q-card">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-display text-lg font-bold text-q-text"><CalendarClock className="h-4 w-4" style={{ color: "rgb(var(--glow-money))" }} /> Coming up</h2>
            <Link to={`/h/${hub.id}/money/bills`} className="text-xs font-semibold text-q-muted hover:text-q-text" data-testid="home-view-bills-link">All bills</Link>
          </div>
          {m.next_bills.length === 0 ? (
            <EmptyState Icon={Receipt} title="No upcoming bills" hint="Add a bill or upload one and we'll keep track of it." rgb="var(--glow-money)" />
          ) : (
            <ul className="divide-y divide-q-border">
              {m.next_bills.map((b) => (
                <li key={b.id} className="flex items-center gap-3 py-3" data-testid={`home-next-bill-${b.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold text-q-text">{b.vendor}</div>
                    <div className="text-xs text-q-faint">{b.category} · due {fmtDate(b.due_date)}</div>
                  </div>
                  <div className="font-display font-bold tabular-nums text-q-text">{money(b.amount)}</div>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="q-card flex flex-col justify-between" style={{ background: "radial-gradient(circle at 100% 0%, rgba(var(--glow-money),0.14), transparent 60%), rgb(var(--q-surface))" }}>
          <div>
            <Wallet className="h-6 w-6" style={{ color: "rgb(var(--glow-money))" }} />
            <h2 className="mt-4 font-display text-xl font-extrabold text-q-text">Got a new bill?</h2>
            <p className="mt-1 text-sm text-q-muted">Snap a photo or drop a PDF. We'll read the vendor, amount and due date for you to check.</p>
          </div>
          <button className="q-btn q-btn-primary mt-6 w-full" disabled={hub.read_only} onClick={onUpload} data-testid="home-upload-bill-btn"><Upload className="h-4 w-4" /> Upload a bill</button>
        </section>
      </div>
    </>
  );
}

function SafetyStrip({ hub }) {
  const items = [["shutoffs", "Shutoffs", ShieldAlert], ["prep", "Emergency prep", Siren], ["manual", "Household manual", BookMarked]].filter(([k]) => canSee(k, hub));
  if (!items.length) return null;
  return (
    <nav className="mb-6 flex flex-wrap gap-2" aria-label="Safety" data-testid="home-safety-strip">
      {items.map(([k, l, I]) => <Link key={k} to={`/h/${hub.id}/${k}`} className="q-btn q-btn-secondary min-h-[44px]" data-testid={`home-safety-${k}`}><I className="h-4 w-4" /> {l}</Link>)}
    </nav>
  );
}

export default function Home() {
  const { hub, reloadHub } = useHub();
  const { session } = useAuth();
  const [data, setData] = useState(null);
  const [upload, setUpload] = useState(false);

  const load = useCallback(() => api.get(`/organizations/${hub.id}/home`).then(({ data }) => setData(data)), [hub.id]);
  useEffect(() => {
    if (hub.my_role !== "kid") load();
  }, [load, hub.my_role]);
  useLiveRefresh(() => hub.my_role !== "kid" && load(), (m) => ["chore", "bill", "spending", "wish", "goal", "piggy.ledger", "list", "list.item", "list.cart", "kid_request", "screen_time", "home"].includes(m.entity), null);

  if (hub.my_role === "kid") return <KidHome name={(session.kid || hub.acting_kid)?.name || ""} hub={hub} />;
  if (!data) return <Loader />;
  const first = session.user.name.split(" ")[0];

  return (
    <div data-testid="home-page">
      <PageHeader eyebrow={hub.name} title={`${greeting()}, ${first}`} testid="home-title" actions={<div className="flex gap-2"><VoiceButton hubId={hub.id} screen="reminders" label="Remind me" /><VoiceButton hubId={hub.id} screen="home" label="Quick add" /></div>} />
      {hub.is_parent && !hub.tenure && !hub.read_only && <section className="q-card mb-4 p-4" data-testid="tenure-banner"><p className="font-semibold text-q-text">Do you rent or own your home?</p><p className="text-sm text-q-muted">We'll switch on the right tools: Lease, Move-in/out log and Maintenance for renters, Housing costs for owners.</p><TenurePicker hub={hub} tenure={null} homeType={null} /></section>}
      <LockAlerts alerts={data.alerts} hubId={hub.id} onDone={load} />
      <TopCards hub={hub} />
      <SafetyStrip hub={hub} />
      <WhoStrip hub={hub} />
      <OnThisDay hub={hub} />
      {hub.is_parent && hub.my_access?.kids && <ApprovalsCard hub={hub} />}
      {data.home_care && <HomeCareCard hub={hub} n={data.home_care.overdue} />}
      {data.money ? <MoneyHome data={data} hub={hub} onUpload={() => setUpload(true)} /> : <WelcomeCard hub={hub} />}
      {upload && <UploadBillDialog open={upload} onOpenChange={setUpload} onSaved={() => { load(); reloadHub(); }} />}
    </div>
  );
}
